import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { logger } from '../utils/logger'
import Seo from '../components/Seo'
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useAuthStore } from '../stores/authStore';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import logo from '../assets/logo/logo.png';

/**
 * Invitation acceptance page
 */
export const AcceptInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Helper to display role name
  const getRoleName = (role: string): string => {
    switch (role) {
      case 'parent_a':
        return 'Parent / Guardian (Admin)';
      case 'parent_b':
        return 'Parent / Guardian';
      case 'caregiver':
        return 'Caregiver';
      case 'child_viewer':
        return 'Viewer';
      default:
        return role;
    }
  };

  // Convex reactive query — fetches invitation by token
  const invitation = useQuery(
    api.invitations.queries.getInvitationByToken,
    token ? { token } : 'skip'
  );

  // Convex mutations
  const acceptInvitationMutation = useMutation(api.invitations.mutations.acceptInvitation);
  const rejectInvitationMutation = useMutation(api.invitations.mutations.rejectInvitation);

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [autoAcceptTriggered, setAutoAcceptTriggered] = useState(false);

  // Determine loading state from Convex query
  const isLoading = invitation === undefined && !error && !success;

  // Determine if invitation is valid for action
  const isExpired = invitation ? invitation.expiresAt < Date.now() : false;
  const isPending = invitation?.status === 'pending' && !isExpired;

  // Auto-accept when user is logged in and invitation is ready
  useEffect(() => {
    if (user && isPending && token && !success && !error && !isProcessing && !autoAcceptTriggered) {
      logger.debug('Auto accepting invitation for logged in user');
      setAutoAcceptTriggered(true);
      handleAccept();
    }
  }, [user, isPending, token, success, error, isProcessing, autoAcceptTriggered]);

  const handleAccept = async () => {
    if (!user || !token) return;

    setIsProcessing(true);
    setError(null);

    try {
      logger.debug('Accepting invitation via Convex');

      await acceptInvitationMutation({ token });

      logger.debug('Invitation accepted');
      setSuccess('Invitation accepted. You will be redirected shortly.');

      // FamilyDataSync will automatically pick up the new family membership
      setTimeout(() => {
        navigate('/calendar');
      }, 2000);
    } catch (err: any) {
      console.error('Acceptance error:', err);
      setError(err.message || 'An error occurred while accepting the invitation');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!token) return;

    setIsProcessing(true);
    setError(null);

    try {
      logger.debug('Rejecting invitation via Convex');

      await rejectInvitationMutation({ token });

      logger.debug('Invitation rejected');
      setSuccess('Invitation rejected. You will be redirected shortly.');

      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      console.error('Rejection error:', err);
      setError(err.message || 'An error occurred while rejecting the invitation');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Layout>
      <Seo title="Invitation — 2homes.app" robots="noindex, nofollow, noarchive" canonical="https://2homes.app/invitation/[token]" />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold mb-6 text-center">Family Invitation</h1>

          {isLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
              <p>Checking invitation...</p>
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <div className="text-red-500 text-5xl mb-4">⚠️</div>
              <h2 className="text-xl font-semibold mb-2">Error</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <div className="space-y-3">
                <button
                  onClick={() => { setError(null); setAutoAcceptTriggered(false); }}
                  className="btn btn-primary"
                >
                  Try again
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="btn btn-outline"
                >
                  Return to home page
                </button>
              </div>
            </div>
          ) : success ? (
            <div className="text-center py-4">
              <div className="text-green-500 text-5xl mb-4">✅</div>
              <h2 className="text-xl font-semibold mb-2">Success</h2>
              <p className="text-gray-600">{success}</p>
            </div>
          ) : !invitation ? (
            <div className="text-center py-4">
              <div className="text-red-500 text-5xl mb-4">⚠️</div>
              <h2 className="text-xl font-semibold mb-2">Invitation Not Found</h2>
              <p className="text-gray-600 mb-4">This invitation link is invalid or has been removed.</p>
              <button
                onClick={() => navigate('/')}
                className="btn btn-outline"
              >
                Return to home page
              </button>
            </div>
          ) : !isPending ? (
            <div className="text-center py-4">
              <div className="text-yellow-500 text-5xl mb-4">⚠️</div>
              <h2 className="text-xl font-semibold mb-2">
                {isExpired ? 'Invitation Expired' : 'Invitation Already Used'}
              </h2>
              <p className="text-gray-600 mb-4">
                {isExpired
                  ? 'This invitation has expired. Please ask the family admin to send a new one.'
                  : `This invitation has already been ${invitation.status}.`}
              </p>
              <button
                onClick={() => navigate('/')}
                className="btn btn-outline"
              >
                Return to home page
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
              <div className="mb-4">
                <img src={logo} alt="2homes.app" className="h-12 w-12 rounded-md shadow-sm mx-auto" />
              </div>
                <h2 className="text-xl font-semibold mb-2">Family Invitation</h2>
                <p className="text-gray-600">
                  You have been invited to join the family as{' '}
                  <span className="font-medium">{getRoleName(invitation.role)}</span>.
                </p>
              </div>

              <div className="border-t border-b py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{invitation.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Expires</p>
                    <p className="font-medium">
                      {format(new Date(invitation.expiresAt), 'dd MMM yyyy')}
                    </p>
                  </div>
                </div>
              </div>

              {!user ? (
                <div className="text-center">
                  <p className="text-gray-600 mb-4">
                    You must create an account to accept the invitation.
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate(`/auth/register?redirect=/invitation/${token}`)}
                      className="w-full neon-button"
                    >
                      Create account
                    </button>
                    <p className="text-sm text-gray-500">
                      Or{' '}
                      <button
                        onClick={() => navigate(`/auth/login?redirect=/invitation/${token}`)}
                        className="text-blue-600 hover:underline"
                      >
                        sign in
                      </button>
                      {' '}if you already have an account
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex space-x-4">
                  <button
                    onClick={handleReject}
                    disabled={isProcessing}
                    className="btn btn-outline flex-1"
                  >
                    {isProcessing ? 'Processing...' : 'Decline'}
                  </button>
                  <button
                    onClick={handleAccept}
                    disabled={isProcessing}
                    className="btn btn-primary flex-1"
                  >
                    {isProcessing ? 'Processing...' : 'Accept'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
